import React, { useState } from 'react'
import { useKPISKUsDetail, useFiscalWeeks } from '../lib/data-service'
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext'
import GlobalHeader from '../components/GlobalHeader'
import { BackButton } from '../components/BackButton'
import { 
  Package, 
  TrendingUp, 
  TrendingDown, 
  Search,
  RefreshCw,
  DollarSign
} from 'lucide-react'

export default function SKUsDetail() {
  const { selectedFiscalWeek } = useFiscalWeekContext()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<'name' | 'revenue' | 'units' | 'avgPrice'>('revenue')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  // Data fetching - get ALL SKUs, not just top 5
  const { data: skus, isLoading, error, refetch } = useKPISKUsDetail(selectedFiscalWeek, 50)

  const handleSort = (field: 'name' | 'revenue' | 'units' | 'avgPrice') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const getPerformanceLevel = (revenue: number) => {
    if (revenue > 5000) return { level: 'Top', color: 'text-green-500 bg-green-500/10' }
    if (revenue > 2000) return { level: 'Strong', color: 'text-blue-500 bg-blue-500/10' }
    if (revenue > 500) return { level: 'Average', color: 'text-yellow-500 bg-yellow-500/10' }
    return { level: 'Under', color: 'text-red-500 bg-red-500/10' }
  }

  const filteredAndSortedSKUs = skus?.filter(sku =>
    sku?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => {
    let aValue: any = a[sortField]
    let bValue: any = b[sortField]
    
    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = 0
    if (bValue === null || bValue === undefined) bValue = 0
    
    if (sortField === 'name') {
      aValue = String(aValue).toLowerCase()
      bValue = String(bValue).toLowerCase()
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1
    } else {
      return aValue < bValue ? 1 : -1
    }
  }) || []

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive mb-4">Failed to load SKU data</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // Check if we have any data
  if (!skus || skus.length === 0) {
    return (
      <div className="space-y-6">
        <GlobalHeader 
          title="SKUs Deep Dive"
          subtitle="No detailed SKU data available"
        />
        
        <div className="bg-muted/50 border border-muted rounded-lg p-8 text-center">
          <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No SKU Data Available</h3>
          <p className="text-muted-foreground mb-4">
            Detailed SKU-level data is not available in the current dataset. 
            The original CSV files contain this data, but only summary data was synced to the database.
          </p>
          <BackButton href="/dashboard" label="Back to Dashboard" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Global Header */}
      <GlobalHeader 
        title="SKUs Deep Dive"
        subtitle="Complete product performance analysis with pricing insights"
        onRefresh={() => refetch()}
        isLoading={isLoading}
      />

      {/* Search Filter */}
      <div className="flex items-center space-x-2 max-w-md">
        <Search className="w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search SKUs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm"
        />
      </div>

      {/* Summary Stats */}
      {filteredAndSortedSKUs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground">Active SKUs</p>
            <p className="text-2xl font-bold text-foreground">{filteredAndSortedSKUs.length}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground">Total Revenue</p>
            <p className="text-2xl font-bold text-foreground">
              {formatCurrency(filteredAndSortedSKUs.reduce((sum, sku) => sum + sku.revenue, 0))}
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground">Total Units</p>
            <p className="text-2xl font-bold text-foreground">
              {formatNumber(filteredAndSortedSKUs.reduce((sum, sku) => sum + sku.units, 0))}
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground">Avg Price</p>
            <p className="text-2xl font-bold text-foreground">
              {filteredAndSortedSKUs.length > 0 ? formatCurrency(
                filteredAndSortedSKUs.reduce((sum, sku) => sum + sku.avgPrice, 0) / filteredAndSortedSKUs.length
              ) : '$0.00'}
            </p>
          </div>
        </div>
      )}

      {/* SKUs Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th 
                  className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Product</span>
                    {sortField === 'name' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th 
                  className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('revenue')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Revenue</span>
                    {sortField === 'revenue' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th 
                  className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('units')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Units</span>
                    {sortField === 'units' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th 
                  className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('avgPrice')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Avg Price</span>
                    {sortField === 'avgPrice' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-muted-foreground">Performance</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-muted-foreground">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading SKU data...
                  </td>
                </tr>
              ) : filteredAndSortedSKUs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-muted-foreground">
                    No SKUs found matching your criteria
                  </td>
                </tr>
              ) : (
                filteredAndSortedSKUs.map((sku, index) => {
                  const performance = getPerformanceLevel(sku.revenue)
                  return (
                    <tr key={sku.name} className="border-t border-border hover:bg-muted/25">
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <Package className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium text-foreground">{sku.name}</span>
                        </div>
                      </td>
                      <td className="p-4 font-medium text-foreground">
                        {formatCurrency(sku.revenue)}
                      </td>
                      <td className="p-4 text-foreground">
                        {formatNumber(sku.units)}
                      </td>
                      <td className="p-4 text-foreground">
                        {formatCurrency(sku.avgPrice)}
                      </td>
                      <td className="p-4">
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${performance.color}`}>
                          {performance.level}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
